// src/pages/dashboards/admin/CompanyDetailModal.jsx
// Company detail + actions, redesigned: avatar header, filled stat tiles,
// scan-pool hero gauge, clean two-column form, admin management.

import React, { useEffect, useState } from "react";
import {
  getCompany, updateCompany,
  createCompanyAdmin, removeCompanyAdmin,
  getScanCredits, allocateScanCredits,
} from "../../../api/admin";
import { Modal, Field, Input, Select, Button, Badge, Loading } from "../../../components/ui";
import { useToast } from "../../../context/ToastContext";
import { date, dateTime, relative, titleCase } from "../../../lib/format";

const STATUSES = ["active", "cancelled", "expired", "past_due"];
const STATUS_TONE = { active: "green", cancelled: "gray", expired: "red", past_due: "amber" };
const TIER_LIMIT = { company_starter: 300, company_pro: 650 };

// filled stat tile, no border
function Tile({ label, value, accent }) {
  return (
    <div className="rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset p-4 text-center">
      <p className="text-[11px] uppercase tracking-wider text-tb-muted">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${accent ? "text-tb-brand" : ""}`}>{value}</p>
    </div>
  );
}

function SectionTitle({ children }) {
  return <p className="text-xs uppercase tracking-wider text-tb-muted mb-3">{children}</p>;
}

export default function CompanyDetailModal({ companyId, onClose, onChanged }) {
  const toast = useToast();
  const [company, setCompany] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [pool, setPool] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({ name: "", email: "", tier: "", status: "", seats: "", term_months: "" });
  const [na, setNa] = useState({ name: "", email: "", password: "" });
  const [creditDelta, setCreditDelta] = useState("");

  const load = async () => {
    try {
      const c = await getCompany(companyId);
      setCompany(c);
      setForm({
        name: c.name || "",
        email: c.email || "",
        tier: c.tier,
        status: c.status,
        seats: c.seats ?? "",
        term_months: "",
      });
      setAdmins(c.admins || []);
      const cr = await getScanCredits({ company_id: companyId });
      if (c.pool) setPool({ ...c.pool, credits: c.pool.credits ?? cr.balance });
      else setPool({ used: null, limit: TIER_LIMIT[c.tier] ?? null, remaining: null, credits: cr.balance });
    } catch (e) {
      toast.error(e.message);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  const saveEdits = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim() ? form.email.trim() : null,
        tier: form.tier,
        status: form.status,
        seats: form.seats === "" ? null : parseInt(form.seats, 10),
      };
      if (form.term_months) payload.term_months = parseInt(form.term_months, 10);
      await updateCompany(companyId, payload);
      toast.success("Company updated");
      onChanged?.();
      load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const addAdmin = async () => {
    if (!na.email.trim() || !na.password) { toast.error("Email and password required"); return; }
    setSaving(true);
    try {
      await createCompanyAdmin(companyId, {
        name: na.name.trim() || na.email.trim(),
        email: na.email.trim(),
        password: na.password,
      });
      toast.success("Admin created");
      setNa({ name: "", email: "", password: "" });
      load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const revokeAdmin = async (userId) => {
    if (!window.confirm("Revoke this admin? They revert to a normal user.")) return;
    setSaving(true);
    try {
      await removeCompanyAdmin(companyId, userId);
      toast.success("Admin revoked");
      load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const applyCredits = async () => {
    const amount = parseInt(creditDelta, 10);
    if (!amount) { toast.error("Enter a non-zero amount"); return; }
    setSaving(true);
    try {
      await allocateScanCredits({ company_id: companyId, amount });
      setCreditDelta("");
      toast.success("Credits updated");
      load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pct = pool && pool.limit && pool.used != null
    ? Math.min(100, Math.round((pool.used / pool.limit) * 100))
    : null;

  const initial = (company?.name || "?").trim().charAt(0).toUpperCase();

  return (
    <Modal open onClose={onClose} title="Company" width="max-w-5xl" bodyClassName="p-6 sm:p-8">
      {!company ? (
        <Loading label="Loading company" />
      ) : (
        <div className="space-y-7">
          {/* identity header */}
          <div className="flex items-center gap-4">
            <span
              className="grid place-items-center h-14 w-14 rounded-2xl text-white text-xl font-bold shrink-0 shadow-green-glow"
              style={{ background: "linear-gradient(135deg,#12B266,#2AD17B)" }}
            >
              {initial}
            </span>
            <div className="min-w-0">
              <h2 className="text-xl font-bold truncate">{company.name}</h2>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Badge tone={STATUS_TONE[company.status]}>{titleCase(company.status)}</Badge>
                <Badge tone="gray">{titleCase(company.tier)}</Badge>
                <span className="text-xs text-tb-muted">Term ends {date(company.expires_at)}</span>
              </div>
            </div>
          </div>

          {/* counts */}
          <div className="grid grid-cols-3 gap-3">
            <Tile label="Linked users" value={company.counts?.linked_users ?? 0} />
            <Tile label="Outstanding" value={company.counts?.claims_outstanding ?? 0} accent={company.counts?.claims_outstanding > 0} />
            <Tile label="Claims paid" value={company.counts?.claims_paid ?? 0} />
          </div>

          {/* two-column main */}
          <div className="grid lg:grid-cols-2 gap-7">
            {/* details */}
            <div>
              <SectionTitle>Details</SectionTitle>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Name"><Input value={form.name} onChange={set("name")} /></Field>
                <Field label="Billing email"><Input type="email" value={form.email} onChange={set("email")} /></Field>
                <Field label="Tier">
                  <Select value={form.tier} onChange={set("tier")}>
                    <option value="company_starter">Company Starter</option>
                    <option value="company_pro">Company Pro</option>
                  </Select>
                </Field>
                <Field label="Status">
                  <Select value={form.status} onChange={set("status")}>
                    {STATUSES.map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}
                  </Select>
                </Field>
                <Field label="Seats" hint="blank = unlimited">
                  <Input type="number" min="1" value={form.seats} onChange={set("seats")} placeholder="unlimited" />
                </Field>
                <Field label="Extend term" hint="months — resets expiry">
                  <Input type="number" min="1" max="60" value={form.term_months} onChange={set("term_months")} placeholder="e.g. 12" />
                </Field>
              </div>
              <div className="flex justify-end mt-4">
                <Button onClick={saveEdits} disabled={saving}>Save changes</Button>
              </div>
            </div>

            {/* scan pool hero */}
            <div>
              <SectionTitle>Scan pool</SectionTitle>
              <div className="rounded-2xl p-5 bg-tb-wash dark:bg-tb-brand/10 border border-tb-brand/20">
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-tb-brand/80">Remaining</p>
                    <p className="text-4xl font-bold text-tb-brand leading-none mt-1">
                      {pool?.remaining != null ? pool.remaining : (pool?.limit ?? "—")}
                    </p>
                  </div>
                  {pool?.credits > 0 && (
                    <span className="text-xs rounded-full bg-white/70 dark:bg-white/10 px-2 py-1 text-tb-brand font-medium">
                      +{pool.credits} credits
                    </span>
                  )}
                </div>

                <div className="mt-4 h-2 rounded-full bg-white/60 dark:bg-black/20 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${pct >= 90 ? "bg-red-500" : "bg-tb-brand"}`}
                    style={{ width: `${pct != null ? pct : 0}%` }}
                  />
                </div>

                <div className="mt-3 flex justify-between text-xs text-tb-muted">
                  <span>Used {pool?.used != null ? pool.used : "—"}</span>
                  <span>Limit {pool?.limit ?? "—"}</span>
                </div>

                {pool?.used == null && (
                  <p className="mt-2 text-[11px] text-tb-muted">Live usage appears once the API reports it.</p>
                )}
              </div>

              <div className="mt-4">
                <Field label="Allocate credits" hint="positive tops up, negative corrects">
                  <div className="flex gap-2">
                    <Input type="number" value={creditDelta} onChange={(e) => setCreditDelta(e.target.value)} placeholder="e.g. 100 or -20" />
                    <Button variant="subtle" onClick={applyCredits} disabled={saving}>Allocate</Button>
                  </div>
                </Field>
              </div>
            </div>
          </div>

          {/* admins */}
          <div className="pt-5 border-t border-tb-border dark:border-tb-d-border">
            <SectionTitle>Admin logins</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-2 mb-4">
              {admins.length === 0 && <p className="text-sm text-tb-muted">No admins yet.</p>}
              {admins.map((a) => (
                <div key={a.id} className="flex items-center justify-between rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{a.name}</p>
                    <p className="text-xs text-tb-muted truncate">{a.email} · {relative(a.last_login_at)}</p>
                  </div>
                  <button
                    onClick={() => revokeAdmin(a.id)}
                    disabled={saving}
                    className="text-xs text-red-600 hover:underline shrink-0 ml-2"
                  >
                    Revoke
                  </button>
                </div>
              ))}
            </div>

            <div className="grid sm:grid-cols-3 gap-2 items-end">
              <Field label="Name"><Input value={na.name} onChange={(e) => setNa({ ...na, name: e.target.value })} placeholder="Jane Doe" /></Field>
              <Field label="Email"><Input type="email" value={na.email} onChange={(e) => setNa({ ...na, email: e.target.value })} placeholder="jane@acme.co.za" /></Field>
              <Field label="Password"><Input type="text" value={na.password} onChange={(e) => setNa({ ...na, password: e.target.value })} placeholder="min 8 chars" /></Field>
            </div>
            <div className="flex justify-end mt-3">
              <Button variant="subtle" onClick={addAdmin} disabled={saving}>Add admin</Button>
            </div>
          </div>

          <p className="text-xs text-tb-muted">Created {dateTime(company.created_at)}</p>
        </div>
      )}
    </Modal>
  );
}